import React, { useMemo, useState } from 'react';
import { organisationsAPI } from '../../../organisations/api/organisationsAPI';
import StudentsTable from '../students/StudentsTable';
import StatCard from '../common/StatCard';
import EmptyState from '../common/EmptyState';
import DrilldownHeader from './DrilldownHeader';
import Segments from './Segments';
import useKeyedRequest from '../../hooks/useKeyedRequest';
import {
  DEFAULT_DOCUMENT_FILTER, DOCUMENT_FILTERS, DOCUMENT_TYPES, documentCounts, filterDocumentRows,
} from '../../lib/documents';
import { DOCUMENT_COLUMNS, DOCUMENT_SORT, nextSort, sortStudents } from '../../lib/studentTable';
import { studentsCsv, csvFilename } from '../../lib/studentsCsv';
import { downloadCsv } from '../../lib/downloadCsv';
import { LIFETIME_CAPTION } from '../../lib/format';

const NO_ROWS = [];

/**
 * Documents on file (mockup state 3): per student of the scope, their
 * current CV, cover letter and personal statement, from GET
 * /api/organisations/documents, which carries no URLs. A version opens the
 * file through a short-lived link made on click (cells DocumentCell). The
 * summary and the filters count the same rows the table shows (lib/documents).
 * Not filtered by the period. The export has status, version and date only.
 *
 * The selected segment is `filter`, from the URL's view (documents:<filter>,
 * lib/overviewView), so a Document coverage row opens the list on its
 * "No …" segment; choosing another calls onFilterChange, which replaces
 * the history entry as switching bucket does.
 */
const DocumentsDrilldown = ({
  scope, scopeLabel, filter = DEFAULT_DOCUMENT_FILTER, onFilterChange, onBack, onOpenStudent,
}) => {
  const [sort, setSort] = useState(DOCUMENT_SORT);
  const request = useKeyedRequest({
    key: scope,
    enabled: Boolean(scope),
    load: () => organisationsAPI.getDocuments({ scope }),
    errorText: 'Could not load documents. Please try again.',
  });
  const rows = request.data?.students || NO_ROWS;

  const counts = useMemo(() => documentCounts(rows), [rows]);
  const sorted = useMemo(
    () => sortStudents(filterDocumentRows(rows, filter), sort, DOCUMENT_COLUMNS),
    [rows, filter, sort],
  );
  const total = rows.length;

  return (
    <div className="td-students td-drill">
      <DrilldownHeader
        title="Documents on file"
        subtitle={[scopeLabel, LIFETIME_CAPTION].filter(Boolean).join(' · ')}
        onBack={onBack}
        exportDisabled={!sorted.length}
        onExport={() => downloadCsv(csvFilename(`${scopeLabel} documents`), studentsCsv(sorted, DOCUMENT_COLUMNS))}
      />
      {request.error ? <div className="td-error">{request.error}</div> : null}
      {request.loading ? <p className="td-loading">Loading…</p> : null}
      {!request.loading && !request.error && total === 0 ? (
        <EmptyState title="No students here yet" />
      ) : null}
      {!request.loading && total > 0 ? (
        <>
          <div className="td-statgrid">
            <StatCard eyebrow="All three" value={counts.filters.all_three.toLocaleString()} caption={total === 1 ? 'student' : 'students'} />
            {DOCUMENT_TYPES.map((t) => (
              <StatCard key={t.key} eyebrow={t.label} value={`${counts.types[t.key]} of ${total}`} caption="uploaded" />
            ))}
          </div>
          <Segments
            label="Show"
            options={DOCUMENT_FILTERS.map((f) => ({ key: f.key, label: f.label, count: counts.filters[f.key] }))}
            value={filter}
            onChange={onFilterChange}
          />
          {sorted.length === 0 ? (
            <EmptyState title="Nobody matches this filter" />
          ) : (
            <StudentsTable
              label="Documents on file"
              students={sorted}
              columns={DOCUMENT_COLUMNS}
              selectable={false}
              sort={sort}
              onSort={(key) => setSort((prev) => nextSort(prev, key, DOCUMENT_COLUMNS))}
              onOpenStudent={onOpenStudent}
            />
          )}
          <p className="td-card__note">
            The current version of each document, whenever it was uploaded. A version opens the file in a
            new tab through a link that lasts five minutes. Sorting by Done puts students with nothing first.
          </p>
        </>
      ) : null}
    </div>
  );
};

export default DocumentsDrilldown;
